"use client"
import { useState } from "react"
import Script from "next/script"
import { zeTrack } from "@/components/Analytics"

interface LeadFormProps {
  listingId: string
  listingTitle: string
  /**
   * Show a WhatsApp shortcut after a successful send. Off by default: on the
   * listing page the sidebar already carries the single WhatsApp button, and
   * rendering one here too put TWO identical "WhatsApp agent" buttons in the
   * same panel.
   */
  showWhatsApp?: boolean
}

const TURNSTILE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

export default function LeadForm({ listingId, listingTitle, showWhatsApp = false }: LeadFormProps) {
  const [form, setForm] = useState({ name: "", phone: "", message: "", isDiaspora: false })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState("")

  async function handleSubmit() {
    if (!form.name || !form.phone) { setError("Please enter your name and phone number"); return }
    setSubmitting(true)
    setError("")
    try {
      let phone = form.phone.replace(/\s/g, "")
      if (phone.startsWith("0")) phone = "+263" + phone.slice(1)
      else if (!phone.startsWith("+")) phone = "+263" + phone
      const turnstileToken = (window as any).turnstile?.getResponse() ?? ""
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listingId, name: form.name, phone, message: form.message, source: "FORM", isDiaspora: form.isDiaspora, "cf-turnstile-response": turnstileToken }),
      })
      const data = await res.json()
      if (data.success) {
        setSubmitted(true)
        zeTrack("enquiry_sent", { listing_id: listingId })
      } else {
        setError(data.error ?? "Something went wrong")
        ;(window as any).turnstile?.reset()
      }
    } catch {
      setError("Something went wrong. Try WhatsApp instead.")
      ;(window as any).turnstile?.reset()
    }
    setSubmitting(false)
  }

  // The counted server-side redirect, never a raw wa.me built here. A direct
  // link skipped the seller's whatsappClicks counter — the number their
  // dashboard and monthly performance email are built on — and required the
  // phone to be in the page for anonymous visitors to scrape.
  const waLink = `/api/go/whatsapp?listing=${encodeURIComponent(listingId)}`

  if (submitted) return (
    <div style={{ background: "rgba(61,139,95,0.06)", border: "1px solid rgba(61,139,95,0.2)", borderRadius: 14, padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
      <div style={{ fontWeight: 700, color: "var(--green-text)", marginBottom: 6, fontSize: 16 }}>Enquiry sent</div>
      <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>The agent will contact you within 1 hour via WhatsApp or call.</div>
      {showWhatsApp && <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: 16, padding: "12px", background: "#075E54", color: "white", borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>Also message on WhatsApp</a>}
    </div>
  )

  return (
    <div>
      <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 600, color: "var(--navy)", marginBottom: 16 }}>Send an enquiry</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label htmlFor="lead-name" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Full name</label>
          <input id="lead-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your full name" autoComplete="name" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
        </div>
        <div>
          <label htmlFor="lead-phone" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Phone number</label>
          <div style={{ display: "flex", gap: 0 }}>
            <div style={{ padding: "12px 12px", border: "1px solid var(--line)", borderRight: "none", borderRadius: "8px 0 0 8px", fontSize: 13, color: "var(--navy)", fontWeight: 600, background: "var(--cream-warm)", whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>+263</div>
            <input id="lead-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="77 234 5678" type="tel" autoComplete="tel" style={{ flex: 1, padding: "12px 14px", border: "1px solid var(--line)", borderRadius: "0 8px 8px 0", fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
          </div>
        </div>
        <div>
          <label htmlFor="lead-message" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Message <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span></label>
          <textarea id="lead-message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="I am interested in this property..." rows={3} style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none", resize: "vertical" }} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" id="lead-diaspora" checked={form.isDiaspora} onChange={e => setForm(f => ({ ...f, isDiaspora: e.target.checked }))} style={{ accentColor: "var(--navy)" }} />
          <span style={{ color: "var(--muted)" }}>I am enquiring from outside Zimbabwe</span>
        </label>
        {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8 }}>{error}</div>}
        {TURNSTILE_KEY && (
          <>
            <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="lazyOnload" />
            <div className="cf-turnstile" data-sitekey={TURNSTILE_KEY} data-error-callback="zeTurnstileError" data-retry="auto" data-theme="light" />
          </>
        )}
        <button onClick={handleSubmit} disabled={submitting} style={{ width: "100%", padding: "13px", background: submitting ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
          {submitting ? "Sending..." : "Send enquiry"}
        </button>
      </div>
      {showWhatsApp && (
        <>
          <div style={{ marginTop: 12, textAlign: "center", fontSize: 12, color: "var(--muted)" }}>Or contact directly</div>
          <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 10, padding: "12px", background: "#075E54", color: "white", borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>WhatsApp agent</a>
        </>
      )}
    </div>
  )
}